/**
 * Student Dashboard — lesson selection page.
 * Matches switchersim-v2 layout: two-column with lessons grid (left)
 * and capstones stacked (right). Multiplayer is a single join button.
 * Data sourced from LESSON_REGISTRY + FEATURES_BY_ROLE in shared-constants.
 */

import { LessonType } from '@graphicsim/shared-types';
import {
  LESSON_REGISTRY,
  FEATURES_BY_ROLE,
  QUIZ_LESSON_IDS,
  SWITCHERSIM,
  INPUT_URLS,
  ROOM,
} from '@graphicsim/shared-constants';
import { navigate } from '../../router';
import type { AuthResult } from '../../router/routeGuard';
import { multiplayerStore } from '../../stores/multiplayerStore';
import * as multiplayerClient from '../../engine/multiplayerClient';
import { swapLiveCameraUrl } from '../../ui/menuSystem';

/** Capstone descriptions (matching switchersim-v2 subtitle pattern) */
const CAPSTONE_DESC: Record<string, string> = {
  'cap-01': 'Basketball Broadcast',
  'cap-02': 'Football Production',
  'cap-03': 'Multi-Sport Challenge',
};

export function renderDashboard(container: HTMLElement, auth: AuthResult): void {
  const features = FEATURES_BY_ROLE[auth.role] ?? FEATURES_BY_ROLE['student'];
  const userName = auth.user
    ? `${auth.user.firstName} ${auth.user.lastName}`.trim()
    : '';
  const roleLabel = auth.role
    ? auth.role.charAt(0).toUpperCase() + auth.role.slice(1)
    : '';

  const coreLessons = LESSON_REGISTRY.filter((l) => l.type === LessonType.CORE);
  const capstones = LESSON_REGISTRY.filter((l) => l.type === LessonType.CAPSTONE);

  // Build quiz ID set for this role
  const quizIds = new Set(features.quizzes ? QUIZ_LESSON_IDS as readonly string[] : []);

  // Build lesson cards — all active for now (pacing comes later)
  const lessonCards = coreLessons.map((l) => {
    const num = l.id.split('-')[1];
    const quizId = `quiz-${l.id}`;
    const hasQuiz = quizIds.has(quizId);
    const quizBadge = hasQuiz ? `<span class="lesson-quiz-badge" data-quiz-id="${quizId}">Quiz</span>` : '';
    return `<button class="lesson-card active" data-lesson-id="${l.id}">
      <span class="lesson-num">${num}.</span>${l.name}${quizBadge}
    </button>`;
  }).join('');

  // Build capstone cards
  let capstonesHtml = '';
  if (features.capstones && capstones.length > 0) {
    capstonesHtml = capstones.map((c) => {
      const desc = CAPSTONE_DESC[c.id] ?? '';
      return `<button class="capstone-card" data-lesson-id="${c.id}">
        <div class="capstone-info">
          <span class="capstone-name">${c.name}</span>
          <span class="capstone-desc">${desc}</span>
        </div>
        <span class="start-label">&#x25B6; START</span>
      </button>`;
    }).join('');
  }

  // User indicator
  let userHtml = '';
  if (userName || roleLabel) {
    const dashboardPath = ['teacher', 'admin', 'manager'].includes(auth.role)
      ? '/dashboard/teacher/dashboard.html'
      : '/dashboard/student/dashboard.html';
    userHtml = `<div class="user-indicator">
      ${userName ? `<span class="user-name">${userName}</span><br>` : ''}
      ${roleLabel ? `<span class="user-role">${roleLabel}</span><br>` : ''}
      <a href="https://simslogin.broadcastsims.com${dashboardPath}" class="dashboard-link">Return to Dashboard</a>
    </div>`;
  }

  // Multiplayer join button
  let multiplayerHtml = '';
  if (features.multiplayer) {
    multiplayerHtml = `<button class="join-multiplayer" id="join-multiplayer-btn">Join Multiplayer Session</button>`;
  }

  container.innerHTML = `<div id="landing-page">
    ${userHtml}
    <div class="landing-title">GraphicSIMS</div>
    <div class="landing-subtitle">NEVCO Pro Video Broadcast Graphics Training</div>
    <div class="landing-columns">
      <div class="lessons-section">
        <div class="section-label">Lessons</div>
        <div class="lessons-grid">
          ${lessonCards}
        </div>
      </div>
      ${capstonesHtml ? `<div class="capstones-section">
        <div class="section-label">Capstones</div>
        ${capstonesHtml}
      </div>` : ''}
    </div>
    ${multiplayerHtml}
    <button class="free-explore-btn" id="free-explore-btn">Free Explore</button>
  </div>`;

  // Wire up lesson card clicks
  container.querySelectorAll<HTMLElement>('.lesson-card.active[data-lesson-id]').forEach((card) => {
    card.addEventListener('click', () => {
      const id = card.dataset.lessonId;
      if (id) navigate(`/lesson/${id}`);
    });
  });

  // Wire up capstone card clicks
  container.querySelectorAll<HTMLElement>('.capstone-card[data-lesson-id]').forEach((card) => {
    card.addEventListener('click', () => {
      const id = card.dataset.lessonId;
      if (id) navigate(`/lesson/${id}`);
    });
  });

  // Wire up quiz badge clicks (prevent card navigation, go to quiz instead)
  container.querySelectorAll<HTMLElement>('.lesson-quiz-badge[data-quiz-id]').forEach((badge) => {
    badge.addEventListener('click', (e) => {
      e.stopPropagation();
      const qid = badge.dataset.quizId;
      if (qid) navigate(`/quiz/${qid}`);
    });
  });

  // Wire up multiplayer join — opens lobby modal
  const multiBtn = container.querySelector('#join-multiplayer-btn');
  if (multiBtn) {
    multiBtn.addEventListener('click', () => {
      showMultiplayerLobby();
    });
  }

  // Wire up free explore
  const freeBtn = container.querySelector('#free-explore-btn');
  if (freeBtn) {
    freeBtn.addEventListener('click', () => navigate('/sim'));
  }
}

// --- Multiplayer Lobby Modal ---

function showMultiplayerLobby(): void {
  // Remove any existing lobby
  const existing = document.getElementById('multiplayer-lobby-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'multiplayer-lobby-overlay';
  overlay.innerHTML = `
    <div class="lobby-card">
      <div class="lobby-title">Join Multiplayer Session</div>
      <div class="lobby-subtitle">Enter the room code from your Technical Director</div>
      <input class="lobby-input" id="lobby-room-code" type="text"
             placeholder="ABC123" maxlength="${ROOM.CODE_LENGTH}" autocomplete="off" spellcheck="false">
      <div class="lobby-error" id="lobby-error"></div>
      <div class="lobby-actions">
        <button class="lobby-btn lobby-join-btn" id="lobby-join">Join as Graphics Operator</button>
        <button class="lobby-btn lobby-cancel-btn" id="lobby-cancel">Cancel</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const input = overlay.querySelector('#lobby-room-code') as HTMLInputElement;
  const errorEl = overlay.querySelector('#lobby-error') as HTMLElement;
  const joinBtn = overlay.querySelector('#lobby-join') as HTMLButtonElement;
  const cancelBtn = overlay.querySelector('#lobby-cancel') as HTMLButtonElement;

  // Focus input
  setTimeout(() => input.focus(), 50);

  // Auto-uppercase
  input.addEventListener('input', () => {
    input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    errorEl.textContent = '';
  });

  // Enter key joins
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') joinBtn.click();
  });

  // Cancel
  cancelBtn.addEventListener('click', () => overlay.remove());

  // Click outside to close
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });

  // Join button
  joinBtn.addEventListener('click', async () => {
    const roomCode = input.value.trim();
    if (roomCode.length !== ROOM.CODE_LENGTH) {
      errorEl.textContent = `Room code must be ${ROOM.CODE_LENGTH} characters.`;
      return;
    }

    joinBtn.disabled = true;
    joinBtn.textContent = 'Joining...';
    errorEl.textContent = '';

    try {
      // REST call to SwitcherSIM to join the room
      const isDev = window.location.hostname === 'localhost';
      const apiBase = isDev ? SWITCHERSIM.API_URL_DEV : SWITCHERSIM.API_URL;
      const wsUrl = isDev ? SWITCHERSIM.WS_URL_DEV : SWITCHERSIM.WS_URL;

      const res = await fetch(`${apiBase}/sessions/${roomCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerName: 'Graphics Operator',
          appType: 'graphics',
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Connection failed' }));
        throw new Error(errData.error || `Join failed (${res.status})`);
      }

      const data = await res.json();
      const playerToken = data.playerToken ?? data.token;

      if (!playerToken) {
        throw new Error('No player token received from server');
      }

      // Store connection info
      multiplayerStore.getState().setRoomCode(roomCode);
      multiplayerStore.getState().setPlayerToken(playerToken);
      multiplayerStore.getState().setVideoUrls(INPUT_URLS);

      // Register program changed handler to swap live camera
      multiplayerClient.onProgramChanged((input, videoUrl) => {
        const url = videoUrl ?? INPUT_URLS[input];
        if (url) {
          swapLiveCameraUrl(url);
        }
      });

      // Connect WebSocket
      multiplayerClient.connect(wsUrl, roomCode, playerToken);

      // Close lobby and navigate to multiplayer lesson
      overlay.remove();
      navigate('/lesson/multi-01');

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to join room';
      errorEl.textContent = msg;
      joinBtn.disabled = false;
      joinBtn.textContent = 'Join as Graphics Operator';
    }
  });
}
